import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Request an equipment transfer — status starts as 'pending'.
 * Any technician or supervisor can initiate one.
 */
export const requestTransfer = async ({ equipment, toOutlet, reason, user }) => {
  const transfer = {
    equipmentId:      equipment.id,
    equipmentSerial:  equipment.serialNumber,
    equipmentType:    equipment.equipmentType,

    fromOutletId:     equipment.outletId,
    fromOutletName:   equipment.outletName,
    toOutletId:        toOutlet.id,
    toOutletName:      toOutlet.outletName,

    reason: reason.trim(),

    initiatedBy:     user.id,
    initiatedByName: user.name,
    initiatedByRole: user.role,

    status: 'pending',

    confirmedBy: null,
    confirmedByName: null,
    confirmedAt: null,

    rejectionReason: null,

    completedBy: null,
    completedByName: null,
    completedAt: null,

    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  const docRef = await addDoc(collection(db, 'transfers'), transfer);
  return { id: docRef.id, ...transfer };
};

/**
 * Supervisor/admin approves a pending transfer.
 * Equipment itself is NOT moved yet — that only happens on completeTransfer,
 * once someone has actually confirmed the equipment physically arrived.
 */
export const approveTransfer = async (transferId, approver) => {
  await updateDoc(doc(db, 'transfers', transferId), {
    status: 'confirmed',
    confirmedBy: approver.id,
    confirmedByName: approver.name,
    confirmedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

/**
 * Supervisor/admin rejects a pending transfer, with a reason.
 */
export const rejectTransfer = async (transferId, approver, reason) => {
  await updateDoc(doc(db, 'transfers', transferId), {
    status: 'rejected',
    confirmedBy: approver.id,
    confirmedByName: approver.name,
    confirmedAt: serverTimestamp(),
    rejectionReason: reason.trim(),
    updatedAt: serverTimestamp()
  });
};

/**
 * Supervisor/admin marks a confirmed transfer as physically completed.
 * THIS is the one moment the equipment's outlet actually changes —
 * two writes, done together, both requiring supervisor+ permissions.
 */
export const completeTransfer = async (transfer, completer) => {
  await updateDoc(doc(db, 'transfers', transfer.id), {
    status: 'completed',
    completedBy: completer.id,
    completedByName: completer.name,
    completedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });

  await updateDoc(doc(db, 'equipment', transfer.equipmentId), {
    outletId: transfer.toOutletId,
    outletName: transfer.toOutletName,
    updatedAt: serverTimestamp()
  });
};

/**
 * Get all transfers initiated by a specific user (technician's own history).
 */
export const getMyTransfers = async (userId) => {
  const q = query(
    collection(db, 'transfers'),
    where('initiatedBy', '==', userId),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

/**
 * Get equipment by serial number — used to look up what's being transferred.
 */
export const findEquipmentBySerial = async (serialNumber) => {
  const q = query(
    collection(db, 'equipment'),
    where('serialNumber', '==', serialNumber.trim())
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
};
